import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { LEGAL_OWNER } from "@/lib/legal";

export type LegalTocItem = { id: string; label: string };

const CROSS_LINKS = [
  { to: "/terminos", label: "Términos y condiciones" },
  { to: "/licencia", label: "Licencia de uso" },
  { to: "/privacidad", label: "Política de privacidad" },
  { to: "/politica-de-reembolsos", label: "Política de reembolsos" },
  { to: "/arrepentimiento", label: "Botón de arrepentimiento" },
  { to: "/contacto", label: "Contacto" },
] as const;

export function LegalPage({
  title,
  updated = LEGAL_OWNER.updated,
  intro,
  toc,
  currentPath,
  children,
}: {
  title: string;
  updated?: string;
  intro?: ReactNode;
  toc?: LegalTocItem[];
  currentPath?: string;
  children: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-3xl px-5 py-16 sm:px-8">
      <p className="eyebrow">Información legal</p>
      <h1 className="mt-3 font-display text-[clamp(1.8rem,4vw,2.8rem)] uppercase leading-tight">
        {title}
      </h1>
      <p className="mt-3 text-xs text-muted-foreground">{updated}</p>
      {intro && <div className="mt-6 text-sm leading-relaxed text-muted-foreground">{intro}</div>}

      {toc && toc.length > 0 && (
        <nav aria-label="Índice del documento" className="surface-card mt-8 p-6">
          <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Índice
          </h2>
          <ol className="mt-4 grid gap-2 text-sm sm:grid-cols-2">
            {toc.map((item, i) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className="rounded-sm text-muted-foreground underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]"
                >
                  {i + 1}. {item.label}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      )}

      <div className="mt-10 space-y-5 text-sm leading-relaxed text-muted-foreground [&_h2]:mt-12 [&_h2]:scroll-mt-24 [&_h2]:text-base [&_h2]:font-semibold [&_h2]:uppercase [&_h2]:tracking-[0.08em] [&_h2]:text-foreground [&_h3]:mt-8 [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:text-foreground [&_li]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4">
        {children}
      </div>

      <div className="mt-14 border-t border-border pt-8">
        <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-muted-foreground">
          Documentos relacionados
        </h2>
        <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          {CROSS_LINKS.filter((l) => l.to !== currentPath).map((l) => (
            <li key={l.to}>
              <Link
                to={l.to}
                className="rounded-sm text-primary underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gold)]"
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
        <p className="mt-6 text-xs leading-relaxed text-muted-foreground">
          {LEGAL_OWNER.brand} · Titular: {LEGAL_OWNER.holder} · CUIT {LEGAL_OWNER.cuit} ·{" "}
          {LEGAL_OWNER.address} ·{" "}
          <a
            href={`mailto:${LEGAL_OWNER.email}`}
            className="text-primary underline underline-offset-4"
          >
            {LEGAL_OWNER.email}
          </a>
        </p>
      </div>
    </div>
  );
}
